import { useState, useEffect, useCallback, useContext } from 'react';
import { UserContext } from '../UserContext';
import { checkPermiso } from '../Utils/PermissionsFunctions';

/**
 * Hook personalizado para manejo de permisos y roles de usuario
 * @param {string[]} permisosRequeridos - Array de nombres de permisos a verificar
 * @returns {Object} - Estado de permisos, roles y funciones de utilidad
 * 
 * @example
 * // Uso básico
 * const { permisos, roles, loading, tienePermiso, tieneRol } = usePermisos([
 *   'doctors_read',
 *   'doctors_update',
 *   'doctor_discount_edit'
 * ]);
 * 
 * // Verificar un permiso específico
 * if (tienePermiso('doctors_update')) {
 *   // Mostrar botón de editar
 * }
 * 
 * // Verificar un rol específico
 * if (tieneRol('admin')) {
 *   // Mostrar panel de administración
 * }
 * 
 * // Verificar múltiples roles
 * if (tieneAlgunRol(['admin', 'doctor'])) {
 *   // Permitir acceso a sección especial
 * }
 */
const usePermisos = (permisosRequeridos = []) => {
    const { user } = useContext(UserContext);
    const [permisos, setPermisos] = useState(() => {
        // Inicializar todos los permisos como null
        return permisosRequeridos.reduce((acc, permiso) => {
            acc[permiso] = null;
            return acc;
        }, {});
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (user?.id && user?.permissions) {
            const nuevosPermisos = permisosRequeridos.reduce((acc, permiso) => {
                acc[permiso] = checkPermiso(permiso, user.permissions);
                return acc;
            }, {});
            
            setPermisos(nuevosPermisos);
            setLoading(false);
        }
    }, [user, permisosRequeridos]);

    /**
     * Verifica si el usuario tiene un permiso específico
     * @param {string} nombrePermiso - Nombre del permiso a verificar
     * @returns {boolean} - true si tiene el permiso, false si no
     */
    const tienePermiso = useCallback((nombrePermiso) => {
        return permisos[nombrePermiso] === true;
    }, [permisos]);

    /**
     * Verifica si el usuario tiene todos los permisos especificados
     * @param {string[]} nombresPermisos - Array de nombres de permisos
     * @returns {boolean} - true si tiene todos los permisos
     */
    const tieneTodosLosPermisos = useCallback((nombresPermisos) => {
        return nombresPermisos.every(permiso => permisos[permiso] === true);
    }, [permisos]);

    /**
     * Verifica si el usuario tiene al menos uno de los permisos especificados
     * @param {string[]} nombresPermisos - Array de nombres de permisos
     * @returns {boolean} - true si tiene al menos uno de los permisos
     */
    const tieneAlgunPermiso = useCallback((nombresPermisos) => {
        return nombresPermisos.some(permiso => permisos[permiso] === true);
    }, [permisos]);

    /**
     * Obtiene una lista de los permisos que el usuario tiene
     * @returns {string[]} - Array de nombres de permisos activos
     */
    const getPermisosActivos = useCallback(() => {
        return Object.entries(permisos)
            .filter(([, value]) => value === true)
            .map(([key]) => key);
    }, [permisos]);

    /**
     * Verifica si el usuario tiene un rol específico
     * @param {string} nombreRol - Nombre del rol a verificar (ej: 'admin', 'doctor', etc.)
     * @returns {boolean} - true si tiene el rol, false si no
     */
    const tieneRol = useCallback((nombreRol) => {
        return user?.user_roles?.includes(nombreRol) || false;
    }, [user]);

    /**
     * Verifica si el usuario tiene todos los roles especificados
     * @param {string[]} nombresRoles - Array de nombres de roles
     * @returns {boolean} - true si tiene todos los roles
     */
    const tieneTodosLosRoles = useCallback((nombresRoles) => {
        if (!user?.user_roles) return false;
        return nombresRoles.every(rol => user.user_roles.includes(rol));
    }, [user]);

    /**
     * Verifica si el usuario tiene al menos uno de los roles especificados
     * @param {string[]} nombresRoles - Array de nombres de roles
     * @returns {boolean} - true si tiene al menos uno de los roles
     */
    const tieneAlgunRol = useCallback((nombresRoles) => {
        if (!user?.user_roles) return false;
        return nombresRoles.some(rol => user.user_roles.includes(rol));
    }, [user]);

    return {
        permisos,
        roles: user?.user_roles || [],
        loading,
        tienePermiso,
        tieneTodosLosPermisos,
        tieneAlgunPermiso,
        getPermisosActivos,
        tieneRol,
        tieneTodosLosRoles,
        tieneAlgunRol,
        user
    };
};

export default usePermisos;
